import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext, CurrentTicketContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import SpyTicketModal from "../SpyTicketModal";
import ActionButton from "../ActionButton";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  ticket: {
    maxWidth: "97%",
    position: "relative",
    display: "grid",
    gridTemplateColumns: "auto 1fr 40%",
    gap: "12px",
    alignItems: "start",
    padding: "12px 16px",
    minHeight: "80px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "8px",
    margin: "4px 8px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.08)",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.24)",
      backgroundColor: "rgba(255, 255, 255, 1)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)",
    },
    // Aumenta especificidade para sobrescrever estilos do Material-UI
    "&.MuiListItem-root": {
      display: "grid",
      padding: "12px 16px",
    },
  },

  // Seção 1: Avatar/Foto
  avatarSection: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  },

  // Seção 2: Dados (nome, mensagem, flags)
  dataSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0, // Permite quebra de texto
  },

  // Seção 3: Botões (limitada a 20% da largura)
  actionsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
    alignItems: "flex-end",
  },

  // Container das flags com quebra de linha
  flagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "4px",
  },

  pendingTicket: {
    cursor: "unset",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.5em",
    whiteSpace: "nowrap",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    textAlign: "right",
    marginTop: "4px",
    marginRight: "20px",
    "& .MuiBadge-badge": {
      fontSize: "10px",
      minWidth: "16px",
      height: "16px",
    },
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : "white",
  },

  lastMessageTime: {
    textAlign: "right",
    fontSize: "12px",
    color: theme.mode === "light" ? "black" : grey[400],
    whiteSpace: "nowrap",
  },

  lastMessageTimeUnread: {
    textAlign: "right",
    fontSize: "12px",
    color: "green",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "0%",
    marginLeft: "5px",
    color: theme.mode === "light" ? "black" : grey[400],
  },

  contactLastMessageUnread: {
    paddingRight: 20,
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : grey[400],
    // width: "50%",
  },

  badgeStyle: {
    color: "white",
    backgroundColor: green[500],
  },

  // Container para os botões de ação (dentro da seção 3)
  actionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "3px",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
  },

  // Container para horário e badge (parte da seção 3)
  timeAndBadgeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
    marginBottom: "8px",
    padding: "4px 0",
  },

  acceptButton: {
    flexDirection: "row",
    height: "auto",
    minHeight: "24px",
    width: "auto",
    fontSize: "10px",
    padding: "4px 8px",
    borderRadius: "12px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    // Aumenta especificidade para sobrescrever estilos do Material-UI
    "&.MuiButton-root": {
      borderRadius: "12px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    "& .MuiButton-label": {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px",
      "& svg": {
        fontSize: "14px",
        marginRight: "1px",
      },
      "& .button-text": {
        fontSize: "9px",
        fontWeight: 600,
        lineHeight: 1.2,
        textTransform: "none",
        whiteSpace: "nowrap",
      },
    },
  },

  ticketQueueColor: {
    flex: "none",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
  },

  ticketInfo: {
    position: "relative",
    top: -13,
  },
  secondaryContentSecond: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignContent: "flex-start",
    gap: "10px",
    marginRight: "10px",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0,
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3,
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  connectionIcon: {
    marginRight: theme.spacing(1),
  },

  // Estilos para o modal da imagem
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  }
}));

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem
  const [spyModalOpen, setSpyModalOpen] = useState(false); // Estado para o modal de espiar

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(CurrentTicketContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Verificar se a finalização com valor de venda está ativa
    if (
      user.finalizacaoComValorVendaAtiva === true ||
      user.finalizacaoComValorVendaAtiva === "true"
    ) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot (abre normalmente)
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Função para espionar ticket sem marcar como lido (abre modal)
  const handleSpyTicketReadOnly = () => {
    setSpyModalOpen(true);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Localização</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        onClick={(e) => {
          console.log("e", e);
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        {/* SEÇÃO 1: FOTO/AVATAR */}
        <div className={classes.avatarSection}>
          <Avatar
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
            }}
            src={`${ticket?.contact?.urlPicture}`}
            className={classes.clickableAvatar}
            onClick={handleImageClick}
          />
        </div>

        {/* SEÇÃO 2: DADOS (Nome, Mensagem, Flags) */}
        <div className={classes.dataSection}>
          {/* Nome do contato */}
          <Typography
            noWrap
            component="div"
            variant="body2"
            className={classes.contactNameWrapper}
          >
            {ticket.isGroup && ticket.channel === "whatsapp" && (
              <GroupIcon
                fontSize="small"
                style={{
                  color: grey[700],
                  marginBottom: "-1px",
                  marginRight: "4px",
                }}
              />
            )}
            {ticket.channel && (
              <ConnectionIcon
                width="20"
                height="20"
                className={classes.connectionIcon}
                connectionType={ticket.channel}
              />
            )}
            {truncate(ticket.contact?.name, 60)}
          </Typography>

          {/* Mensagem */}
          <Typography
            className={
              Number(ticket.unreadMessages) > 0
                ? classes.contactLastMessageUnread
                : classes.contactLastMessage
            }
            noWrap
            component="div"
            variant="body2"
          >
            {renderLastMessage()}
          </Typography>

          {/* Container das flags com quebra de linha */}
          <div className={classes.flagsContainer}>
            {ticket?.whatsapp && (
              <Badge
                className={classes.connectionTag}
                style={{
                  backgroundColor:
                    ticket.channel === "whatsapp"
                      ? ticket.whatsapp?.color || "#25D366"
                      : ticket.channel === "facebook"
                      ? "#4267B2"
                      : "#E1306C",
                }}
              >
                {ticket.whatsapp?.name.toUpperCase()}
              </Badge>
            )}

            <Badge
              style={{
                backgroundColor: ticket.queue?.color || "#7c7c7c",
              }}
              className={classes.connectionTag}
            >
              {ticket.queueId
                ? ticket.queue?.name.toUpperCase()
                : ticket.status === "lgpd"
                ? "LGPD"
                : `${i18n.t("momentsUser.noqueue")}`}
            </Badge>

            {ticket?.user && (
              <Badge
                style={{ backgroundColor: "#000000" }}
                className={classes.connectionTag}
              >
                {ticket.user?.name.toUpperCase()}
              </Badge>
            )}

            {ticket?.contact?.tags?.map((tag) => (
              <ContactTag
                tag={tag}
                key={`ticket-contact-tag-${ticket.id}-${tag.id}`}
              />
            ))}

            {ticket.tags?.map((tag) => (
              <ContactTag
                tag={tag}
                key={`ticket-tag-${ticket.id}-${tag.id}`}
              />
            ))}
          </div>
        </div>

        {/* SEÇÃO 3: BOTÕES E HORÁRIO */}
        <div className={classes.actionsSection}>
          {/* Badge e Horário */}
          <div className={classes.timeAndBadgeContainer}>
            <Badge
              className={classes.newMessagesCount}
              badgeContent={shouldBlurMessages ? "?" : ticket.unreadMessages}
              classes={{
                badge: classes.badgeStyle,
              }}
            />

            {ticket.lastMessage && (
              <Typography
                className={
                  Number(ticket.unreadMessages) > 0
                    ? classes.lastMessageTimeUnread
                    : classes.lastMessageTime
                }
                component="span"
                variant="body2"
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                  <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                ) : (
                  <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                )}
              </Typography>
            )}
          </div>

          {/* Container dos botões */}
          <div className={classes.actionsContainer}>
            {/* Botão Espiar - chatbot (abre normalmente) */}
            {ticket.status === "chatbot" && (
              <ActionButton
                icon={<VisibilityIcon />}
                text={i18n.t("tickets.buttons.spy")}
                colorType="spy"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpyTicket();
                }}
              />
            )}

            {/* Botão Espiar - open/group (sem marcar como lido) */}
            {(ticket.status === "open" || ticket.status === "group") && (
              <ActionButton
                icon={<VisibilityIcon />}
                text={i18n.t("tickets.buttons.spy")}
                colorType="spy"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpyTicketReadOnly();
                }}
              />
            )}

            {/* Botão Aceitar - sem fila */}
            {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) && (
              <ActionButton
                icon={<Done />}
                text={i18n.t("ticketsList.buttons.accept")}
                colorType="accept"
                loading={loading}
                onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
              />
            )}

            {/* Botão Aceitar - com fila */}
            {ticket.status === "pending" && ticket.queueId !== null && (
              <ActionButton
                icon={<Done />}
                text={i18n.t("ticketsList.buttons.accept")}
                colorType="accept"
                loading={loading}
                onClick={(e) => handleAcepptTicket(ticket.id)}
              />
            )}

            {/* Botão Transferir */}
            {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
              <ActionButton
                icon={<SwapHoriz />}
                text={i18n.t("tickets.buttons.transfer")}
                colorType="transfer"
                loading={loading}
                onClick={handleOpenTransferModal}
              />
            )}

            {/* Botão Resolver */}
            {(ticket.status === "open" || ticket.status === "group") && (
              <ActionButton
                icon={<HighlightOff />}
                text={i18n.t("messagesList.header.buttons.resolve")}
                colorType="resolve"
                loading={loading}
                onClick={(e) => handleCloseTicket(ticket.id)}
              />
            )}

            {/* Botão Ignorar */}
            {(ticket.status === "pending" || ticket.status === "lgpd") && (user.userClosePendingTicket === "enabled" || user.profile === "admin") && (
              <ActionButton
                icon={<HighlightOff />}
                text={i18n.t("ticketsList.buttons.ignore")}
                colorType="reject"
                loading={loading}
                onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
              />
            )}

            {/* Botão Novo Ticket */}
            {ticket.status === "closed" && (
              <ActionButton
                icon={<Add />}
                text={i18n.t("ticketsList.buttons.createNew")}
                colorType="create"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenNewTicketModal();
                }}
              />
            )}
          </div>
        </div>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img
            src={ticket?.contact?.urlPicture}
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Espiar */}
      {spyModalOpen && (
        <SpyTicketModal
          open={spyModalOpen}
          onClose={() => setSpyModalOpen(false)}
          ticket={ticket}
        />
      )}
    </React.Fragment>
  );
};

export default React.memo(TicketListItemCustom);